/**
 * Device Data Adapter - Convert API Telemetry to Device-Specific Formats
 *
 * This utility transforms raw telemetry data from the backend API into the
 * specific formats required by each device calculation utility.
 *
 * Version: 2.0.0
 * Updated: January 23, 2026
 */

import { TelemetryReading } from '../types';

// Import types from calculation utilities
import type { MotionEvent } from './motionCalculations';
import type { DoorEvent } from './doorCalculations';
import type { CameraEvent } from './cameraCalculations';
import type { EnergyEvent } from './energyCalculations';
import type { OutletEvent } from './powerCalculations';
import type { ActuatorEvent } from './actuatorCalculations';
import type { AirQualityReading } from './airQualityCalculations';
import type { LightReading } from './lightCalculations';
import type { PressureReading } from './pressureCalculations';
import type { GasReading } from './gasCalculations';
import type { SmokeReading } from './smokeCalculations';
import type { WaterLeakReading } from './waterLeakCalculations';

/**
 * Convert telemetry readings to Motion Events
 */
export const telemetryToMotionEvents = (readings: TelemetryReading[]): MotionEvent[] => {
  return readings
    .filter((r) => r.motion !== undefined)
    .map((r) => ({
      timestamp: new Date(r.timestamp || r.time || Date.now()),
      detected: r.motion === true || (r.motion as any) === 1 || (r.motion as any) === '1',
      confidence: r.motionConfidence || 0.9,
      zone: r.zone || 'main',
      duration: r.motionDuration || 0,
    }));
};

/**
 * Convert telemetry readings to Door Events
 */
export const telemetryToDoorEvents = (readings: TelemetryReading[]): DoorEvent[] => {
  return readings
    .filter((r) => r.doorOpen !== undefined)
    .map((r) => ({
      timestamp: new Date(r.timestamp || r.time || Date.now()),
      state: r.doorOpen ? 'open' : 'closed',
      isOpen: r.doorOpen === true || (r.doorOpen as any) === 1 || (r.doorOpen as any) === '1',
      duration: r.doorDuration || 0,
      source: r.source || 'sensor',
    }));
};

/**
 * Convert telemetry readings to Camera Events
 */
export const telemetryToCameraEvents = (readings: TelemetryReading[]): CameraEvent[] => {
  return readings
    .filter((r) => r.cameraRecording !== undefined)
    .map((r) => ({
      timestamp: new Date(r.timestamp || r.time || Date.now()),
      type: r.cameraEventType || 'motion',
      eventType: r.cameraEventType || 'motion',
      recordingActive:
        r.cameraRecording === true ||
        (r.cameraRecording as any) === 1 ||
        (r.cameraRecording as any) === '1',
      duration: r.cameraDuration || 0,
      storageUsedMB: r.cameraStorageUsedMB || 0,
    }));
};

/**
 * Convert telemetry readings to Energy Events
 */
export const telemetryToEnergyReadings = (readings: TelemetryReading[]): EnergyEvent[] => {
  return readings
    .filter((r) => r.energyKwh !== undefined || r.powerW !== undefined)
    .map((r) => ({
      timestamp: new Date(r.timestamp || r.time || Date.now()),
      powerW: r.powerW || 0,
      voltage: r.voltage || 220,
      current: r.current || (r.powerW ? r.powerW / 220 : 0),
      energyKwh: r.energyKwh || 0,
      frequency: r.frequency || 50,
      powerFactor: r.powerFactor || 0.9,
    }));
};

/**
 * Convert telemetry readings to Power Events (Outlet)
 */
export const telemetryToPowerEvents = (readings: TelemetryReading[]): OutletEvent[] => {
  return readings
    .filter((r) => r.actuatorState !== undefined || r.powerW !== undefined)
    .map((r) => ({
      timestamp: new Date(r.timestamp || r.time || Date.now()),
      state: r.actuatorState === 'on' || r.actuatorState === '1' ? 'on' : 'off',
      powerW: r.powerW || 0,
      voltage: r.voltage || 220,
      current: r.current || 0,
      triggeredBy: r.source || 'manual',
    }));
};

/**
 * Convert telemetry readings to Actuator Events
 */
export const telemetryToActuatorEvents = (readings: TelemetryReading[]): ActuatorEvent[] => {
  return readings
    .filter((r) => r.actuatorState !== undefined)
    .map((r) => ({
      timestamp: new Date(r.timestamp || r.time || Date.now()),
      state: String(r.actuatorState || 'unknown') as any,
      targetState: r.actuatorTargetState,
      reason: (r.actuatorReason as 'alarm' | 'manual' | 'schedule' | 'automation') || 'manual',
      duration: r.actuatorDuration || 0,
    }));
};

/**
 * Convert telemetry readings to Air Quality Readings
 */
export const telemetryToAirQualityReadings = (
  readings: TelemetryReading[],
): AirQualityReading[] => {
  return readings
    .filter((r) => r.aqi !== undefined || r.pm25 !== undefined || r.co2 !== undefined)
    .map((r) => ({
      timestamp: new Date(r.timestamp || r.time || Date.now()),
      aqi: r.aqi,
      pm25: r.pm25,
      pm10: r.pm10,
      co2: r.co2,
      co: r.co,
      voc: r.voc,
      temperature: r.temperature,
      humidity: r.humidity,
    }));
};

/**
 * Convert telemetry readings to Light Readings
 */
export const telemetryToLightReadings = (readings: TelemetryReading[]): LightReading[] => {
  return readings
    .filter((r) => r.lux !== undefined)
    .map((r) => ({
      timestamp: new Date(r.timestamp || r.time || Date.now()),
      lux: r.lux || 0,
      uvIndex: r.uvIndex,
      colorTemperature: r.colorTemperature,
    }));
};

/**
 * Convert telemetry readings to Pressure Readings
 */
export const telemetryToPressureReadings = (readings: TelemetryReading[]): PressureReading[] => {
  return readings
    .filter((r) => r.pressure !== undefined)
    .map((r) => ({
      pressure: r.pressure || 1013,
      timestamp: new Date(r.timestamp || r.time || Date.now()),
      temperature: r.temperature,
    }));
};

/**
 * Convert telemetry readings to Gas Readings
 */
export const telemetryToGasReadings = (readings: TelemetryReading[]): GasReading[] => {
  return readings
    .filter((r) => r.gasLevel !== undefined)
    .map((r) => ({
      gasType: (r.gasType as any) || 'CO',
      concentration: r.gasLevel || 0,
      timestamp: new Date(r.timestamp || r.time || Date.now()),
      temperature: r.temperature,
      humidity: r.humidity,
    }));
};

/**
 * Convert telemetry readings to Smoke Readings
 */
export const telemetryToSmokeReadings = (readings: TelemetryReading[]): SmokeReading[] => {
  return readings
    .filter((r) => r.smokeDensity !== undefined)
    .map((r) => ({
      density: r.smokeDensity || 0,
      time: new Date(r.timestamp || r.time || Date.now()),
      timestamp: new Date(r.timestamp || r.time || Date.now()),
      temperature: r.temperature,
      co: r.co,
    }));
};

/**
 * Convert telemetry readings to Water Leak Readings
 */
export const telemetryToWaterLeakReadings = (readings: TelemetryReading[]): WaterLeakReading[] => {
  return readings
    .filter((r) => r.waterLevel !== undefined || r.moisture !== undefined)
    .map((r) => ({
      waterDetected: (r.waterLevel || 0) > 0,
      waterLevel: r.waterLevel || 0,
      moisture: r.moisture || 0,
      time: new Date(r.timestamp || r.time || Date.now()),
      timestamp: new Date(r.timestamp || r.time || Date.now()),
    }));
};

/**
 * Extract current state from latest reading
 */

// Motion sensor
export const extractCurrentMotionState = (reading: any): boolean => {
  return reading?.motion === true || reading?.motion === 1;
};

// Door sensor
export const extractCurrentDoorState = (reading: any): boolean => {
  return reading?.doorOpen === true || reading?.doorOpen === 1;
};

// Power outlet
export const extractCurrentPowerState = (reading: any): 'on' | 'off' => {
  return reading?.actuatorState === 'on' || reading?.actuatorState === '1' ? 'on' : 'off';
};

// Actuator
export const extractCurrentActuatorState = (reading: any): string => {
  return reading?.actuatorState || 'off';
};

/**
 * Time filtering helpers
 */

export const filterReadingsByHours = (
  readings: TelemetryReading[],
  hours: number,
): TelemetryReading[] => {
  const cutoffTime = new Date(Date.now() - hours * 60 * 60 * 1000);
  return readings.filter((r) => {
    const timestamp = new Date(r.timestamp || r.time || 0);
    return timestamp >= cutoffTime;
  });
};

export const filterReadingsToday = (readings: TelemetryReading[]): TelemetryReading[] => {
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  return readings.filter((r) => {
    const timestamp = new Date(r.timestamp || r.time || 0);
    return timestamp >= startOfToday;
  });
};

export const filterReadingsYesterday = (readings: TelemetryReading[]): TelemetryReading[] => {
  const startOfYesterday = new Date();
  startOfYesterday.setDate(startOfYesterday.getDate() - 1);
  startOfYesterday.setHours(0, 0, 0, 0);

  const endOfYesterday = new Date();
  endOfYesterday.setHours(0, 0, 0, 0);

  return readings.filter((r) => {
    const timestamp = new Date(r.timestamp || r.time || 0);
    return timestamp >= startOfYesterday && timestamp < endOfYesterday;
  });
};

// Export default object with all functions
export default {
  telemetryToMotionEvents,
  telemetryToDoorEvents,
  telemetryToCameraEvents,
  telemetryToEnergyReadings,
  telemetryToPowerEvents,
  telemetryToActuatorEvents,
  telemetryToAirQualityReadings,
  telemetryToLightReadings,
  telemetryToPressureReadings,
  telemetryToGasReadings,
  telemetryToSmokeReadings,
  telemetryToWaterLeakReadings,
  extractCurrentMotionState,
  extractCurrentDoorState,
  extractCurrentPowerState,
  extractCurrentActuatorState,
  filterReadingsByHours,
  filterReadingsToday,
  filterReadingsYesterday,
};
